<template>
  <div :class="joinClasses('task', [dasherize(task.status)])">
    <div class="task__item">
      <Checkbox
        :id="`task_${task.id}_status`"
        :disabled="canEdit"
        :modelValue="taskComplete"
        :modifiers="['rounded', task.status]"
        @update:modelValue="markTaskAsComplete"
      >
        <div class="typo-tiny task__item-type">
          {{ $t(`tasks.type_names.${task.task_type}`) }}
        </div>
      </Checkbox>
      <div class="task__item-details">
        <div class="task__item-details-description typo-field-text">
          {{ task.description }}
        </div>

        <div class="task__item-details-info typo-tiny">
          <div
            v-if="task.labels.length > 0"
            class="task__item-details-info-section"
          >
            <div
              v-for="label in task.labels"
              :key="label"
              class="task__label"
            >
              {{ label }}
            </div>
          </div>

          <div
            v-if="taskIncomplete && task.due_date"
            class="task__item-details-info-section"
          >
            {{ $t('tasks.task_due') }}
            {{ formatDate(task.due_date) }}
          </div>

          <div
            v-if="taskComplete || taskNotApplicable"
            class="task__item-details-info-section"
          >
            {{ $t(`tasks.${taskComplete ? 'completed_by' : 'set_as_applicable'}`) }}
            {{ task.closed_by?.full_name }}
            {{ $t('tasks.task_on') }}
            {{ formatDate(task.closed_at) }}
          </div>

          <div
            v-if="task.external_reference"
            class="task__item-details-info-section task__item-details-reference"
          >
            {{ task.external_reference }}
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!canEdit"
      class="task__menu"
    >
      <div class="dropdown">
        <Popper
          v-model:open="menuOpen"
          placement="bottom-end"
        >
          <template #trigger>
            <div class="dropdown__trigger">
              <UseSvg
                class="icon"
                name="vertical_ellipsis"
              />
            </div>
          </template>

          <div class="menu menu--dropdown">
            <ul class="menu__items">
              <li class="menu-item typo-list-item menu-item--dropdown">
                <button
                  class="menu-item__link"
                  type="button"
                  @click="editTask"
                >
                  {{ $t('tasks.edit_task') }}
                </button>
              </li>
              <li
                v-if="taskNotApplicable"
                class="menu-item typo-list-item menu-item--dropdown"
              >
                <button
                  class="menu-item__link"
                  type="button"
                  @click="markTaskAsApplicable"
                >
                  {{ $t('tasks.mark_as_applicable') }}
                </button>
              </li>
              <li
                v-else
                :class="{
                  'menu-item typo-list-item menu-item--dropdown': true,
                  'menu-item--disabled': taskComplete,
                }"
              >
                <button
                  class="menu-item__link"
                  type="button"
                  @click="markTaskAsNotApplicable"
                >
                  {{ $t('tasks.mark_as_not_applicable') }}
                </button>
              </li>
              <li
                :class="{
                  'menu-item typo-list-item menu-item--dropdown menu-item--destructive': true,
                  'menu-item--disabled': taskComplete,
                }"
              >
                <button
                  class="menu-item__link"
                  type="button"
                  @click="deleteTask"
                >
                  <p>{{ $t('tasks.delete_task') }}</p>
                </button>
              </li>
            </ul>
          </div>
        </Popper>
      </div>
    </div>

    <IssueTaskFormModal
      v-if="editModalOpen"
      :errorSaving="errorSaving"
      :initialTask="task"
      :inspectionDate="issue.inspection.date"
      :issue="issue"
      @cancel="editModalOpen = false"
      @submit="updateTask"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

import joinClasses from '@/functions/join_classes';
import { formatDate } from '@/functions/date_format_helpers';
import dasherize from '@/functions/dasherize';

import Popper from '@/components/generic/popper.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Checkbox from '@/components/form/checkbox.vue';

import IssueTaskFormModal from '@/components/issues/issue_task_form_modal.vue';

const props = defineProps({
  task: { type: Object, required: true },
  issue: { type: Object, required: true },
  canEdit: { type: Boolean, required: false },
  open: { type: Boolean, required: false, default: false },
  useServiceToClose: { type: Boolean, required: false, default: false },
  errorSaving: { type: Boolean, required: false, default: false },
});
const emit = defineEmits(['update', 'markAs', 'delete']);

const menuOpen = ref(false);
const editModalOpen = ref(false);

const taskComplete = computed(() => {
  return props.task.status === 'complete';
});

const taskIncomplete = computed(() => {
  return props.task.status === 'incomplete';
});

const taskNotApplicable = computed(() => {
  return props.task.status === 'not_applicable';
});

watch(() => props.open, (value) => {
  value !== null && (editModalOpen.value = value);
});

function editTask() {
  editModalOpen.value = true;
  menuOpen.value = false;
  props.task.isNew = false;
}

function markTaskAsComplete(complete) {
  emit('markAs', complete ? 'complete' : 'incomplete');
}

function markTaskAsNotApplicable() {
  menuOpen.value = false;
  emit('markAs', 'not_applicable');
}

function markTaskAsApplicable() {
  menuOpen.value = false;
  emit('markAs', 'incomplete');
}

function deleteTask() {
  menuOpen.value = false;
  emit('delete');
}

function updateTask(task) {
  !props.useServiceToClose && (editModalOpen.value = false);
  emit('update', task);
}
</script>
